import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  styled,
  Avatar,
  Collapse,
  Chip
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import {
  isSectionAllowedForSubAdmin,
  isItemAllowedForSubAdmin,
} from '../users/adminModuleMap';
import { mainApi } from '../../config/apiClient';
import CloseIcon from '@mui/icons-material/Close';
import SidebarSettingsModal from './SidebarSettingsModal';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import NewReleasesIcon from '@mui/icons-material/NewReleases';
import { BRAND } from '../../config/brand';

import DashboardIcon from '@mui/icons-material/Dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import MessageIcon from '@mui/icons-material/Message';
import PollIcon from '@mui/icons-material/Poll';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import BusinessIcon from '@mui/icons-material/Business';
import EventSeatIcon from '@mui/icons-material/EventSeat';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import SettingsIcon from '@mui/icons-material/Settings';
import EventIcon from '@mui/icons-material/Event';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PersonIcon from '@mui/icons-material/Person';
import InventoryIcon from '@mui/icons-material/Inventory';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import ReceiptIcon from '@mui/icons-material/Receipt';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ShoppingBagIcon from '@mui/icons-material/ShoppingBag';
import SendIcon from '@mui/icons-material/Send';
import GetAppIcon from '@mui/icons-material/GetApp';
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange';
import DescriptionIcon from '@mui/icons-material/Description';
import StorefrontIcon from '@mui/icons-material/Storefront';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import SupervisedUserCircleIcon from '@mui/icons-material/SupervisedUserCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SchoolIcon from '@mui/icons-material/School';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ArticleIcon from '@mui/icons-material/Article';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import TableRestaurantIcon from '@mui/icons-material/TableRestaurant';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining';
import TouchAppIcon from '@mui/icons-material/TouchApp';
import DevicesIcon from '@mui/icons-material/Devices';
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import BuildIcon from '@mui/icons-material/Build';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BarChartIcon from '@mui/icons-material/BarChart';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import CollectionsIcon from '@mui/icons-material/Collections';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';

const SIDEBAR_VISIBLE_BUTTONS_KEY = 'gts_sidebar_visible_buttons';
const SIDEBAR_COLOR_KEY = 'gts_sidebar_color';
const SIDEBAR_VISIBLE_HEADERS_KEY = 'gts_sidebar_visible_headers';
const SIDEBAR_VISIBLE_ITEMS_KEY = 'gts_sidebar_visible_items';
const SIDEBAR_SETTINGS_CHANGED_EVENT = 'gts-sidebar-settings-changed';
const TASK_TRACKING_OPEN_KEY = 'gts_task_tracking_open';
const ACCOUNTING_OPEN_KEY = 'gts_accounting_open';
const INVOICE_OPEN_KEY = 'gts_invoice_open';
const OFFER_OPEN_KEY = 'gts_offer_open';
const SALES_SYSTEM_OPEN_KEY = 'gts_sales_system_open';
const HR_OPEN_KEY = 'gts_hr_open';
const WMS_OPEN_KEY = 'gts_wms_open';
const DEVICE_MANAGEMENT_OPEN_KEY = 'gts_device_management_open';
const CARD_MANAGEMENT_OPEN_KEY = 'gts_card_management_open';
const BRANCHES_OPEN_KEY = 'gts_branches_open';
const OTHER_OPEN_KEY = 'gts_other_open';

const TASK_TRACKING_BUTTONS = [
  { id: 'dashboard', label: 'Dashboard', path: '/dashboard', icon: <DashboardIcon /> },
  { id: 'tasks', label: 'Görevler', path: '/tasks', icon: <AssignmentIcon /> },
  { id: 'reports', label: 'Raporlar', path: '/reports', icon: <AssessmentIcon /> },
  { id: 'messages', label: 'Mesajlar', path: '/messages', icon: <MessageIcon /> },
  { id: 'surveys', label: 'Anketler', path: '/surveys', icon: <PollIcon /> },
];

const ACCOUNTING_BUTTONS = [
  { id: 'dashboard', label: 'Dashboard', path: '/accounting/dashboard', icon: <DashboardIcon /> },
  { id: 'current', label: 'Cari', path: '/cari', icon: <PersonIcon /> },
  { id: 'stock', label: 'Ürün ve Stok', path: '/stok', icon: <InventoryIcon /> },
  // Etiket yazdırma menüden kaldırıldı; Stok ekranındaki üç nokta menüsünde.
  { id: 'bank', label: 'Banka', path: '/banka', icon: <AccountBalanceIcon /> },
  { id: 'cash', label: 'Kasa', path: '/kasa', icon: <LocalAtmIcon /> },
  { id: 'warehouse', label: 'Depo', path: '/depo', icon: <WarehouseIcon /> },
  { id: 'currency', label: 'Döviz', path: '/doviz', icon: <CurrencyExchangeIcon /> },
  { id: 'branchPrices', label: 'Şube Fiyatları', path: '/sube-fiyatlari', icon: <LocalOfferIcon /> },
  { id: 'imageLibrary', label: 'Resim Kütüphanesi', path: '/resim-kutuphanesi', icon: <CollectionsIcon /> },
];

const INVOICE_SUBMENU = [
  { id: 'alis-fatura', label: 'Alış Faturası', path: '/fatura/alis', icon: <ShoppingCartIcon /> },
  { id: 'satis-fatura', label: 'Satış Faturası', path: '/fatura/satis', icon: <ShoppingBagIcon /> },
];

const OFFER_SUBMENU: { id: string; label: string; path: string; icon: React.ReactNode }[] = [];

const HR_BUTTONS: { id: string; label: string; path: string; icon: React.ReactNode }[] = [
  { id: 'personnel', label: 'Personel', path: '/personnel', icon: <PeopleIcon /> },
  { id: 'shifts', label: 'Vardiya', path: '/shifts', icon: <AccessTimeIcon /> },
  { id: 'pdks', label: 'PDKS', path: '/hr/pdks', icon: <FingerprintIcon /> },
  { id: 'leaveManagement', label: 'İzin Yönetimi', path: '/ik/izin', icon: <EventAvailableIcon /> },
  { id: 'performanceManagement', label: 'Performans Yönetimi', path: '/ik/performans', icon: <TrendingUpIcon /> },
  { id: 'trainingDevelopment', label: 'Eğitim ve Gelişim', path: '/ik/egitim', icon: <SchoolIcon /> },
  { id: 'payroll', label: 'Bordro ve Ödemeler', path: '/ik/bordro', icon: <RequestQuoteIcon /> },
  { id: 'documentManagement', label: 'Döküman Yönetimi', path: '/ik/dokuman', icon: <ArticleIcon /> },
  { id: 'recruitment', label: 'İşe Alım Yönetimi', path: '/ik/isealim', icon: <PersonAddIcon /> },
  { id: 'cvApplications', label: 'İş Başvuruları', path: '/hr/cv-application', icon: <DescriptionIcon /> },
];

const TEMP_HIDDEN_HR_BUTTON_IDS = new Set([
  'leaveManagement',
  'performanceManagement',
  'trainingDevelopment',
  'payroll',
  'documentManagement',
]);

const SALES_SYSTEM_BUTTONS: { id: string; label: string; path: string; icon: React.ReactNode; requireHacioglu?: boolean }[] = [
  { id: 'kitchenScreen', label: 'Mutfak Ekranı', path: '/mutfak-ekrani-secimi', icon: <RestaurantIcon /> },
  { id: 'fastSale', label: 'Hızlı Satış', path: '/hizli-satis', icon: <FlashOnIcon /> },
  { id: 'restaurant', label: 'Restoran', path: '/restoran', icon: <RestaurantIcon /> },
  { id: 'orderManagement', label: 'Paket Yönetimi', path: '/siparis-yonetimi', icon: <LocalShippingIcon /> },
  { id: 'qrMenu', label: 'QR Menü', path: '/qr-menu-management', icon: <QrCode2Icon /> },
  { id: 'kioskMenu', label: 'Kiosk Menü', path: '/kiosk-menu', icon: <TouchAppIcon /> },
  { id: 'eCommerce', label: 'E-Ticaret', path: '/e-commerce', icon: <ShoppingBasketIcon /> },
  { id: 'courier', label: 'Kurye', path: '/kurye', icon: <DeliveryDiningIcon /> },
  { id: 'salesReports', label: 'Raporlar', path: '/gun-sonu', icon: <AssessmentIcon /> },
  { id: 'integrations', label: 'Entegrasyonlar', path: '/entegrasyonlar', icon: <IntegrationInstructionsIcon /> },
  { id: 'haciogluMapping', label: 'Hacıoğlu Ürün Eşleştirme', path: '/hacioglu-urun-eslestirme', icon: <CompareArrowsIcon />, requireHacioglu: true },
  // clubEntrance kaldırıldı — kulüp girişi artık hızlı satış modu olarak yönetiliyor.
  { id: 'imageLibrary', label: 'Resim Kütüphanesi', path: '/resim-kutuphanesi', icon: <CollectionsIcon /> },
];

// Akıllı Depo WMS altındaki butonlar
const WMS_BUTTONS: { id: string; label: string; path: string; icon: React.ReactNode }[] = [
  { id: 'wmsPanel', label: 'WMS Paneli', path: '/wms/panel', icon: <DashboardIcon /> },
  { id: 'wmsWarehouseSetup', label: 'Depo Tanımlama', path: '/wms/depo-tanimlama', icon: <WarehouseIcon /> },
  { id: 'wmsOrderPool', label: 'Sipariş Havuzu', path: '/wms/siparis-havuzu', icon: <ShoppingCartIcon /> },
  { id: 'wmsWaves', label: 'İş Emirleri', path: '/wms/is-emirleri', icon: <AssignmentIcon /> },
  { id: 'wmsPickingSetup', label: 'Toplama', path: '/wms/toplama-ayarlari', icon: <PlaylistAddCheckIcon /> },
  { id: 'wmsCancelMgmt', label: 'İptal Yönetimi', path: '/wms/iptal-yonetimi', icon: <CancelOutlinedIcon /> },
  { id: 'wmsPackingSetup', label: 'Paketleme', path: '/wms/paketleme-ayarlari', icon: <Inventory2Icon /> },
  { id: 'wmsReceivingSetup', label: 'Mal Kabul', path: '/wms/mal-kabul-ayarlari', icon: <MoveToInboxIcon /> },
  { id: 'wmsCycleCountSetup', label: 'Stok Sayım', path: '/wms/stok-sayim-ayarlari', icon: <FactCheckIcon /> },
  { id: 'wmsReceivingMgmt', label: 'Mal Kabul Yönetimi', path: '/wms/mal-kabul-yonetimi', icon: <LocalShippingIcon /> },
  { id: 'wmsCycleCountMgmt', label: 'Stok Sayım Yönetimi', path: '/wms/stok-sayim-yonetimi', icon: <InventoryIcon /> },
  { id: 'wmsEquipment', label: 'Ekipman Yönetimi', path: '/wms/ekipman-yonetimi', icon: <BuildIcon /> },
  { id: 'wmsReports', label: 'WMS Raporları', path: '/wms/raporlar', icon: <BarChartIcon /> },
];

const OTHER_BUTTONS = [
  { id: 'events', label: 'Etkinlikler', path: '/events', icon: <EventIcon /> },
  { id: 'reservations', label: 'Rezervasyon', path: '/reservations', icon: <EventSeatIcon /> },
  { id: 'mailReports', label: 'Otomatik Mail Raporları', path: '/accounting/mail-raporlari', icon: <MarkEmailReadIcon /> },
  // Çoklu admin sistemi: yalnız ana admin (role==='admin') görür — render'da filtrelenir.
  { id: 'users', label: 'Kullanıcılar', path: '/kullanicilar', icon: <PeopleIcon /> },
];

// Geçici gizlenen "Diğer" butonları. Yalnızca 'events' (Etkinlikler) henüz
// SQL'e taşınmadığı için menüde gizli. Taşınınca buradan çıkarılacak. ('reservations' SQL'e taşındı — açıldı.)
const TEMP_HIDDEN_OTHER_BUTTON_IDS = new Set([
  'events',
]);

const DEVICE_MANAGEMENT_BUTTONS = [
  { id: 'deviceManagement', label: 'Cihaz Yönetimi', path: '/cihaz-yonetimi', icon: <DevicesIcon /> },
  { id: 'scales', label: 'Tartılar', path: '/tartilar', icon: <MonitorWeightIcon /> },
];

const CARD_MANAGEMENT_BUTTONS = [
  { id: 'cards', label: 'Kart Tanımları', path: '/kart-yonetimi/kartlar', icon: <CreditCardIcon /> },
  { id: 'accessManagement', label: 'Geçiş Yönetimi', path: '/kart-yonetimi/gecis-yonetimi', icon: <MeetingRoomIcon /> },
];

const BRANCHES_BUTTONS = [
  { id: 'branches', label: 'Şubeler', path: '/branches', icon: <BusinessIcon /> },
];

const MenuContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'var(--sidebar-color, #102648)',
  color: 'var(--sidebar-text-color, white)',
}));

const MenuHeader = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: '1px solid rgba(255,255,255,0.1)',
}));

const MenuList = styled(List)(({ theme }) => ({
  flexGrow: 1,
  padding: theme.spacing(2, 1),
  overflowY: 'auto',
}));

const MenuItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  '&.active': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderLeft: '4px solid var(--sidebar-text-color, white)',
  },
}));

const GroupHeader = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  backgroundColor: 'rgba(255,255,255,0.05)',
  fontWeight: 'bold',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
}));

const SubMenuItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  paddingLeft: theme.spacing(4),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  backgroundColor: 'rgba(255,255,255,0.03)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  '&.active': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderLeft: '4px solid var(--sidebar-text-color, white)',
  },
}));

const NestedSubMenuItem = styled(ListItem)(({ theme }) => ({
  padding: theme.spacing(1.5, 2),
  paddingLeft: theme.spacing(6),
  marginBottom: theme.spacing(0.5),
  borderRadius: theme.spacing(1),
  cursor: 'pointer',
  backgroundColor: 'rgba(255,255,255,0.02)',
  '&:hover': {
    backgroundColor: 'rgba(255,255,255,0.1)',
  },
  '&.active': {
    backgroundColor: 'rgba(255,255,255,0.1)',
    borderLeft: '4px solid var(--sidebar-text-color, white)',
  },
}));

const MenuFooter = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderTop: '1px solid rgba(255,255,255,0.1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
}));

interface MobileMenuProps {
  onClose: () => void;
}

const MobileMenu: React.FC<MobileMenuProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, currentUser, userDetails, enabledModules, isModuleEnabled, isSubAdmin, adminPanelPermissions } = useAuth();
  const { t } = useTranslation('common2');
  const [enableHaciogluExport, setEnableHaciogluExport] = useState(false);
  
  // Menü state'leri - localStorage'dan al
  const [taskTrackingOpen, setTaskTrackingOpen] = useState(() => {
    const saved = localStorage.getItem(TASK_TRACKING_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) true dön, aksi halde kaydedilen değeri dön
    return saved === null ? true : saved === 'true';
  });
  
  const [accountingOpen, setAccountingOpen] = useState(() => {
    const saved = localStorage.getItem(ACCOUNTING_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) true dön, aksi halde kaydedilen değeri dön
    return saved === null ? true : saved === 'true';
  });
  
  const [invoiceOpen, setInvoiceOpen] = useState(() => {
    const saved = localStorage.getItem(INVOICE_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });
  
  const [offerOpen, setOfferOpen] = useState(() => {
    const saved = localStorage.getItem(OFFER_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });

  // Satış Sistemi menüsünün açık/kapalı durumu - localStorage'dan al
  const [salesSystemOpen, setSalesSystemOpen] = useState(() => {
    const saved = localStorage.getItem(SALES_SYSTEM_OPEN_KEY);
    return saved === null ? false : saved === 'true';
  });
  
  // İnsan Kaynakları menüsünün açık/kapalı durumu - localStorage'dan al
  const [hrOpen, setHrOpen] = useState(() => {
    const saved = localStorage.getItem(HR_OPEN_KEY);
    // null ise (ilk kez kullanıyorsa) false dön, aksi halde kaydedilen değeri dön
    return saved === null ? false : saved === 'true';
  });

  // WMS menüsünün açık/kapalı durumu - localStorage'dan al
  const [wmsOpen, setWmsOpen] = useState(() => {
    const saved = localStorage.getItem(WMS_OPEN_KEY);
    return saved === null ? false : saved === 'true';
  });

  // Cihaz Yönetimi menüsünün açık/kapalı durumu - localStorage'dan al
  const [deviceManagementOpen, setDeviceManagementOpen] = useState(() => {
    const saved = localStorage.getItem(DEVICE_MANAGEMENT_OPEN_KEY);
    return saved === null ? false : saved === 'true';
  });

  // Şubeler menüsünün açık/kapalı durumu - localStorage'dan al
  const [branchesOpen, setBranchesOpen] = useState(() => {
    const saved = localStorage.getItem(BRANCHES_OPEN_KEY);
    return saved === null ? false : saved === 'true';
  });

  const [otherOpen, setOtherOpen] = useState(() => {
    const saved = localStorage.getItem(OTHER_OPEN_KEY);
    return saved === null ? false : saved === 'true';
  });

  const [cardManagementOpen, setCardManagementOpen] = useState(() => {
    const saved = localStorage.getItem(CARD_MANAGEMENT_OPEN_KEY);
    return saved === null ? false : saved === 'true';
  });

  const [userVisibleSections, setUserVisibleSections] = useState(() => {
    const defaults = {
      task_tracking: true,
      accounting: true,
      sales_system: true,
      hr: true,
      wms: true,
      device_management: true,
      card_management: true,
      branches: true,
      other: true,
    };
    const savedHeadersStr = localStorage.getItem(SIDEBAR_VISIBLE_HEADERS_KEY);
    if (savedHeadersStr) {
      try {
        const headers: string[] = JSON.parse(savedHeadersStr);
        return {
          task_tracking: headers.includes('task_tracking'),
          accounting: headers.includes('accounting'),
          sales_system: headers.includes('sales_system'),
          hr: headers.includes('hr'),
          wms: headers.includes('wms'),
          device_management: headers.includes('device_management'),
          card_management: headers.includes('card_management'),
          branches: headers.includes('branches'),
          other: headers.includes('other'),
        };
      } catch {
        return defaults;
      }
    }
    return defaults;
  });

  // Efektif görünürlük = kullanıcı tercihi VE şirket modülü açık (SuperAdmin modül kontrolü)
  // VE (subadmin ise) adminPanel izin haritasında en az bir buton izinli (deny-by-default).
  const visibleSections = React.useMemo(() => {
    const out: { [key: string]: boolean } = {};
    for (const key of Object.keys(userVisibleSections)) {
      out[key] =
        (userVisibleSections as { [key: string]: boolean })[key] &&
        isModuleEnabled(key) &&
        (!isSubAdmin || isSectionAllowedForSubAdmin(adminPanelPermissions, key));
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userVisibleSections, enabledModules, isSubAdmin, adminPanelPermissions]);

  const [visibleButtons, setVisibleButtons] = useState<string[]>(() => {
    const defaultButtons = [
      ...TASK_TRACKING_BUTTONS.map(btn => btn.id),
      ...ACCOUNTING_BUTTONS.map(btn => btn.id),
      ...OTHER_BUTTONS.map(btn => btn.id),
      ...INVOICE_SUBMENU.map(btn => btn.id),
      ...OFFER_SUBMENU.map(btn => btn.id),
      ...SALES_SYSTEM_BUTTONS.map(btn => btn.id),
      ...HR_BUTTONS.map(btn => btn.id),
      ...WMS_BUTTONS.map(btn => btn.id),
      ...DEVICE_MANAGEMENT_BUTTONS.map(btn => btn.id),
      ...CARD_MANAGEMENT_BUTTONS.map(btn => btn.id),
      ...BRANCHES_BUTTONS.map(btn => btn.id),
    ];
    const savedVisibleButtons = localStorage.getItem(SIDEBAR_VISIBLE_BUTTONS_KEY);
    if (savedVisibleButtons) {
      try {
        const parsedButtons = JSON.parse(savedVisibleButtons);
        // Kayıtlı butonları ve varsayılan butonları birleştir, tekrar edenleri kaldır
        // Set'i Array.from ile diziye çevir
        return [...Array.from(new Set([...defaultButtons, ...parsedButtons]))]; 
      } catch (error) {
        console.error('Error parsing visible buttons from localStorage:', error);
        return defaultButtons; 
      }
    } else {
      return defaultButtons;
    }
  });

  const [visibleItems, setVisibleItems] = useState<Record<string, string[]>>(() => {
    const saved = localStorage.getItem(SIDEBAR_VISIBLE_ITEMS_KEY);
    if (saved) {
      try {
        return JSON.parse(saved) as Record<string, string[]>;
      } catch {
        return {};
      }
    }
    return {};
  });
  
  // Sidebar ayarları modalı için
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  
  // Aktif sayfa kontrolü
  const isActive = (path: string) => location.pathname === path;

  // Yeni eklenen ogeler — kullanicinin localStorage listesinde olmasa bile
  // default visible olacak. Sidebar.tsx ile uyumlu davranis.
  const NEW_DEFAULT_VISIBLE_ITEMS: Record<string, string[]> = {
    accounting: ['loans', 'projects', 'interCompanyB2B'],
    other: ['mailReports'],
    wms: ['wmsCancelMgmt'],
    card_management: ['accessManagement'],
    device_management: ['scales'],
    hr: ['pdks'],
  };

  const SETTINGS_MANAGED_ITEM_IDS: Record<string, string[]> = {
    accounting: [
      'dashboard', 'current', 'stock', 'bank', 'cash', 'checksNotes', 'loans', 'projects', 'invoice', 'eInvoice', 'offers', 'waybills', 'orders', 'interCompanyB2B',
      'warehouse', 'currency', 'branchPrices', 'imageLibrary',
      'accounting-dashboard', 'cari', 'stok', 'banka', 'kasa', 'depo', 'doviz', 'cek-senet', 'krediler', 'projeler', 'fatura', 'e-fatura', 'teklifler', 'irsaliyeler', 'siparisler', 'firmalar-arasi-b2b'
    ],
    sales_system: [
      'kitchenScreen', 'fastSale', 'restaurant', 'orderManagement', 'qrMenu', 'kioskMenu', 'eCommerce', 'courier', 'salesReports', 'integrations', 'haciogluMapping', 'imageLibrary',
      'hizli-satis', 'restorant', 'qr-menu', 'e-commerce'
    ],
    task_tracking: ['dashboard', 'tasks', 'reports', 'messages', 'surveys'],
    hr: ['personnel', 'shifts', 'pdks', 'leaveManagement', 'performanceManagement', 'trainingDevelopment', 'payroll', 'documentManagement', 'recruitment', 'cvApplications', 'ik-izin', 'ik-performans', 'ik-egitim', 'ik-bordro', 'ik-dokuman', 'ik-isealim'],
    wms: ['wmsPanel', 'wmsWarehouseSetup', 'wmsOrderPool', 'wmsWaves', 'wmsPickingSetup', 'wmsCancelMgmt', 'wmsPackingSetup', 'wmsReceivingSetup', 'wmsCycleCountSetup', 'wmsReceivingMgmt', 'wmsCycleCountMgmt', 'wmsEquipment', 'wmsReports'],
    device_management: ['deviceManagement', 'scales'],
    card_management: ['cards', 'accessManagement'],
    branches: ['branches'],
    other: ['events', 'reservations', 'mailReports'],
  };

  const isItemVisible = (sectionId: string, itemId: string, aliases: string[] = []) => {
    // Çoklu admin sistemi: subadmin deny-by-default — izin haritasında açıkça
    // işaretlenmemiş her buton gizlenir. 'Kullanıcılar' butonu subadmin'de asla görünmez.
    if (isSubAdmin) {
      if (sectionId === 'other' && itemId === 'users') return false;
      if (!isItemAllowedForSubAdmin(adminPanelPermissions, sectionId, itemId)) return false;
    }
    const sectionItems = visibleItems[sectionId];
    // Sidebar'daki davranışla uyum: ayar yoksa görünür kabul et
    if (!sectionItems) return true;
    const managedIds = SETTINGS_MANAGED_ITEM_IDS[sectionId] || [];
    const relevantIds = [itemId, ...aliases].filter(id => managedIds.includes(id));
    // Bu item modal tarafindan yonetilmiyorsa gizleme uygulama
    if (relevantIds.length === 0) return true;
    if (relevantIds.some(id => sectionItems.includes(id))) return true;
    // Kayitli liste var ama yeni eklenen oge listede yok → default goster
    if (NEW_DEFAULT_VISIBLE_ITEMS[sectionId]?.includes(itemId)) return true;
    return false;
  };

  const getLegacyAliases = (sectionId: string, itemId: string): string[] => {
    if (sectionId === 'accounting') {
      const map: Record<string, string[]> = {
        dashboard: ['accounting-dashboard'],
        current: ['cari'],
        stock: ['stok'],
        bank: ['banka'],
        cash: ['kasa'],
        warehouse: ['depo'],
        currency: ['doviz'],
      };
      return map[itemId] || [];
    }

    if (sectionId === 'sales_system') {
      const map: Record<string, string[]> = {
        fastSale: ['hizli-satis'],
        restaurant: ['restorant'],
        qrMenu: ['qr-menu'],
        eCommerce: ['e-commerce'],
      };
      return map[itemId] || [];
    }

    if (sectionId === 'hr') {
      const map: Record<string, string[]> = {
        leaveManagement: ['ik-izin'],
        performanceManagement: ['ik-performans'],
        trainingDevelopment: ['ik-egitim'],
        payroll: ['ik-bordro'],
        documentManagement: ['ik-dokuman'],
        recruitment: ['ik-isealim'],
      };
      return map[itemId] || [];
    }

    return [];
  };
  
  // Sayfa geçişi
  const handleNavigation = (path: string) => {
    navigate(path);
    onClose();
  };
  
  // Çıkış yapma
  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
      onClose();
    } catch (error) {
      console.error('Çıkış yapılırken hata oluştu:', error);
    }
  };
  
  // Ayarlar modalını aç
  const handleOpenSettings = () => {
    setSettingsModalOpen(true);
  };
  
  // Görev Takip menüsünü aç/kapat
  const toggleTaskTracking = () => {
    const newState = !taskTrackingOpen;
    setTaskTrackingOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(TASK_TRACKING_OPEN_KEY, String(newState));
  };
  
  // Ön Muhasebe menüsünü aç/kapat
  const toggleAccounting = () => {
    const newState = !accountingOpen;
    setAccountingOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(ACCOUNTING_OPEN_KEY, String(newState));
  };
  
  // Fatura alt menüsünü aç/kapat
  const toggleInvoice = (e: React.MouseEvent) => {
    e.stopPropagation(); // Ana menünün toggle olmasını engelle
    const newState = !invoiceOpen;
    setInvoiceOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(INVOICE_OPEN_KEY, String(newState));
  };
  
  // Teklifler alt menüsünü aç/kapat
  const toggleOffer = (e: React.MouseEvent) => {
    e.stopPropagation(); // Ana menünün toggle olmasını engelle
    const newState = !offerOpen;
    setOfferOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(OFFER_OPEN_KEY, String(newState));
  };

  // Satış Sistemi menüsünü aç/kapat
  const toggleSalesSystem = () => {
    const newState = !salesSystemOpen;
    setSalesSystemOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(SALES_SYSTEM_OPEN_KEY, String(newState));
  };

  // İnsan Kaynakları menüsünü aç/kapat
  const toggleHr = () => {
    const newState = !hrOpen;
    setHrOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(HR_OPEN_KEY, String(newState));
  };

  // WMS menüsünü aç/kapat
  const toggleWms = () => {
    const newState = !wmsOpen;
    setWmsOpen(newState);
    localStorage.setItem(WMS_OPEN_KEY, String(newState));
  };

  // Cihaz Yönetimi menüsünü aç/kapat
  const toggleDeviceManagement = () => {
    const newState = !deviceManagementOpen;
    setDeviceManagementOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(DEVICE_MANAGEMENT_OPEN_KEY, String(newState));
  };

  // Şubeler menüsünü aç/kapat
  const toggleBranches = () => {
    const newState = !branchesOpen;
    setBranchesOpen(newState);
    // Durumu localStorage'a kaydet
    localStorage.setItem(BRANCHES_OPEN_KEY, String(newState));
  };

  const toggleOther = () => {
    const newState = !otherOpen;
    setOtherOpen(newState);
    localStorage.setItem(OTHER_OPEN_KEY, String(newState));
  };

  const toggleCardManagement = () => {
    const newState = !cardManagementOpen;
    setCardManagementOpen(newState);
    localStorage.setItem(CARD_MANAGEMENT_OPEN_KEY, String(newState));
  };

  useEffect(() => {
    const fetchCompanySettings = async () => {
      if (!userDetails?.companyId) {
        setEnableHaciogluExport(false);
        return;
      }
      try {
        const response = await mainApi.get(`/api/Company`);
        const data = response.data;
        setEnableHaciogluExport(data.enableHaciogluExport || false);
      } catch {
        setEnableHaciogluExport(false);
      }
    };
    fetchCompanySettings();
  }, [userDetails?.companyId]);

  useEffect(() => {
    const syncFromStorage = () => {
      setTaskTrackingOpen(localStorage.getItem(TASK_TRACKING_OPEN_KEY) === 'true');
      setAccountingOpen(localStorage.getItem(ACCOUNTING_OPEN_KEY) === 'true');
      setInvoiceOpen(localStorage.getItem(INVOICE_OPEN_KEY) === 'true');
      setOfferOpen(localStorage.getItem(OFFER_OPEN_KEY) === 'true');
      setSalesSystemOpen(localStorage.getItem(SALES_SYSTEM_OPEN_KEY) === 'true');
      setHrOpen(localStorage.getItem(HR_OPEN_KEY) === 'true');
      setWmsOpen(localStorage.getItem(WMS_OPEN_KEY) === 'true');
      setDeviceManagementOpen(localStorage.getItem(DEVICE_MANAGEMENT_OPEN_KEY) === 'true');
      setBranchesOpen(localStorage.getItem(BRANCHES_OPEN_KEY) === 'true');
      setOtherOpen(localStorage.getItem(OTHER_OPEN_KEY) === 'true');
      setCardManagementOpen(localStorage.getItem(CARD_MANAGEMENT_OPEN_KEY) === 'true');

      const savedHeadersStr = localStorage.getItem(SIDEBAR_VISIBLE_HEADERS_KEY);
      if (savedHeadersStr) {
        try {
          const headers: string[] = JSON.parse(savedHeadersStr);
          setUserVisibleSections({
            task_tracking: headers.includes('task_tracking'),
            accounting: headers.includes('accounting'),
            sales_system: headers.includes('sales_system'),
            hr: headers.includes('hr'),
            wms: headers.includes('wms'),
            device_management: headers.includes('device_management'),
            card_management: headers.includes('card_management'),
            branches: headers.includes('branches'),
            other: headers.includes('other'),
          });
        } catch (error) {
          console.error('Error parsing visible headers from localStorage:', error);
        }
      }

      const savedVisibleItems = localStorage.getItem(SIDEBAR_VISIBLE_ITEMS_KEY);
      if (savedVisibleItems) {
        try {
          setVisibleItems(JSON.parse(savedVisibleItems) as Record<string, string[]>);
        } catch (error) {
          console.error('Error parsing visible items from localStorage:', error);
          setVisibleItems({});
        }
      } else {
        setVisibleItems({});
      }

      const defaultButtons = [
        ...TASK_TRACKING_BUTTONS.map(btn => btn.id),
        ...ACCOUNTING_BUTTONS.map(btn => btn.id),
        ...OTHER_BUTTONS.map(btn => btn.id),
        ...INVOICE_SUBMENU.map(btn => btn.id),
        ...OFFER_SUBMENU.map(btn => btn.id),
        ...SALES_SYSTEM_BUTTONS.map(btn => btn.id),
        ...HR_BUTTONS.map(btn => btn.id),
        ...WMS_BUTTONS.map(btn => btn.id),
        ...DEVICE_MANAGEMENT_BUTTONS.map(btn => btn.id),
        ...CARD_MANAGEMENT_BUTTONS.map(btn => btn.id),
        ...BRANCHES_BUTTONS.map(btn => btn.id),
      ];
      const savedVisibleButtons = localStorage.getItem(SIDEBAR_VISIBLE_BUTTONS_KEY);
      if (savedVisibleButtons) {
        try {
          const parsedButtons = JSON.parse(savedVisibleButtons);
          setVisibleButtons([...Array.from(new Set([...defaultButtons, ...parsedButtons]))] as string[]);
        } catch (error) {
          console.error('Error parsing visible buttons from localStorage:', error);
          setVisibleButtons(defaultButtons);
        }
      } else {
        setVisibleButtons(defaultButtons);
      }
    };

    // Mount aninda da guncel localStorage degerlerini uygula
    syncFromStorage();

    window.addEventListener('storage', syncFromStorage);
    window.addEventListener(SIDEBAR_SETTINGS_CHANGED_EVENT, syncFromStorage);
    return () => {
      window.removeEventListener('storage', syncFromStorage);
      window.removeEventListener(SIDEBAR_SETTINGS_CHANGED_EVENT, syncFromStorage);
    };
  }, []);

  return (
    <MenuContainer>
      <MenuHeader>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Avatar src={BRAND.logos.main} alt={`${BRAND.name} Logo`} sx={{ width: 40, height: 40, mr: 2 }} />
          <Typography variant="h6" fontWeight="bold">
            {BRAND.copyrightHolder}
          </Typography>
        </Box>
        <IconButton 
          onClick={onClose} 
          sx={{ color: 'var(--sidebar-text-color, white)' }}
        >
          <CloseIcon />
        </IconButton>
      </MenuHeader>
      
      <MenuList>
        {/* Yönetici Ekranı — subadmin'de ayrı izne tabidir (admin_screen.adminScreen) */}
        {(!isSubAdmin || isItemAllowedForSubAdmin(adminPanelPermissions, 'admin_screen', 'adminScreen')) && (
        <MenuItem 
          className={isActive('/accounting/dashboard') ? 'active' : ''}
          onClick={() => handleNavigation('/accounting/dashboard')}
          sx={{ 
            mb: 1,
            backgroundColor: isActive('/accounting/dashboard') 
              ? 'rgba(255,255,255,0.1)' 
              : 'rgba(76, 175, 80, 0.1)',
            '&:hover': {
              backgroundColor: isActive('/accounting/dashboard')
                ? 'rgba(255,255,255,0.15)'
                : 'rgba(76, 175, 80, 0.2)',
            },
          }}
        >
          <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
            <RocketLaunchIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t('menu.adminScreen', { ns: 'accounting' })}
            primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
          />
        </MenuItem>
        )}

        {/* Ön Muhasebe */}
        {visibleSections.accounting && (
          <>
            <GroupHeader onClick={toggleAccounting} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <AccountBalanceWalletIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('menu.preAccounting', { ns: 'accounting' })}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {accountingOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={accountingOpen} timeout="auto" unmountOnExit>
              {ACCOUNTING_BUTTONS
                .filter(b => !['warehouse', 'currency', 'branchPrices', 'imageLibrary'].includes(b.id))
                .filter(button => isItemVisible('accounting', button.id, getLegacyAliases('accounting', button.id)))
                .map((button) => (
                <SubMenuItem 
                  key={button.id}
                  className={isActive(button.path) ? 'active' : ''}
                  onClick={() => handleNavigation(button.path)}
                >
                  <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                    {button.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={t(`menu.accountingButtons.${button.id}`, {
                      ns: 'accounting',
                      defaultValue: t(`sidebar.accountingButtons.${button.id}`)
                    })}
                    primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                  />
                </SubMenuItem>
              ))}
              {isItemVisible('accounting', 'checksNotes', ['cek-senet']) && (
              <SubMenuItem onClick={() => handleNavigation('/cek-senet')}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <ReceiptLongIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.checksNotes')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                />
              </SubMenuItem>
              )}
              {isItemVisible('accounting', 'loans', ['krediler']) && (
              <SubMenuItem onClick={() => handleNavigation('/krediler')}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <RequestQuoteIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.loans')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                />
              </SubMenuItem>
              )}
              {isItemVisible('accounting', 'projects', ['projeler']) && (
              <SubMenuItem onClick={() => handleNavigation('/projeler')}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <AccountTreeIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.projects')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                />
              </SubMenuItem>
              )}
              {isItemVisible('accounting', 'invoice', ['fatura']) && (
              <SubMenuItem onClick={() => handleNavigation('/fatura/alis')}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <ReceiptIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.invoice')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                />
              </SubMenuItem>
              )}
              {isItemVisible('accounting', 'eInvoice', ['e-fatura']) && (
              <SubMenuItem onClick={() => handleNavigation('/e-fatura')}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <DescriptionIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('menu.eInvoice', { ns: 'accounting' })}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                />
              </SubMenuItem>
              )}
              {isItemVisible('accounting', 'offers', ['teklifler']) && (
              <SubMenuItem onClick={() => handleNavigation('/teklifler')}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <LocalOfferIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.offers')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                />
              </SubMenuItem>
              )}
              {isItemVisible('accounting', 'waybills', ['irsaliyeler']) && (
              <SubMenuItem onClick={() => handleNavigation('/irsaliyeler')}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <LocalShippingIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.waybills')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                />
              </SubMenuItem>
              )}
              {isItemVisible('accounting', 'orders', ['siparisler']) && (
              <SubMenuItem onClick={() => handleNavigation('/siparisler')}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <ShoppingCartIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.orders')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                />
              </SubMenuItem>
              )}
              {ACCOUNTING_BUTTONS
                .filter(b => ['warehouse', 'currency', 'branchPrices', 'imageLibrary'].includes(b.id))
                .filter(button => isItemVisible('accounting', button.id, getLegacyAliases('accounting', button.id)))
                .map((button) => (
                <SubMenuItem 
                  key={button.id}
                  className={isActive(button.path) ? 'active' : ''}
                  onClick={() => handleNavigation(button.path)}
                >
                  <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                    {button.icon}
                  </ListItemIcon>
                  <ListItemText 
                    primary={t(`menu.accountingButtons.${button.id}`, {
                      ns: 'accounting',
                      defaultValue: t(`sidebar.accountingButtons.${button.id}`)
                    })}
                    primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                  />
                </SubMenuItem>
              ))}
            </Collapse>
          </>
        )}

        {/* Satış Sistemi */}
        {visibleSections.sales_system && (
          <>
            <GroupHeader onClick={toggleSalesSystem} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <StorefrontIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.salesSystem')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {salesSystemOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={salesSystemOpen} timeout="auto" unmountOnExit>
              {SALES_SYSTEM_BUTTONS.map((button) => {
                if (button.requireHacioglu && !enableHaciogluExport) return null;
                if (!isItemVisible('sales_system', button.id, getLegacyAliases('sales_system', button.id))) return null;
                return (
                  <SubMenuItem 
                    key={button.id}
                    className={isActive(button.path) ? 'active' : ''}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`sidebar.salesSystemButtons.${button.id}`)}
                      primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                    />
                  </SubMenuItem>
                );
              })}
            </Collapse>
          </>
        )}

        {/* Görev Takip */}
        {visibleSections.task_tracking && (
          <>
            <GroupHeader onClick={toggleTaskTracking} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <TrackChangesIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.taskTracking')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {taskTrackingOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={taskTrackingOpen} timeout="auto" unmountOnExit>
              {TASK_TRACKING_BUTTONS.map((button) => 
                visibleButtons.includes(button.id) &&
                isItemVisible('task_tracking', button.id) && (
                  <SubMenuItem 
                    key={button.id}
                    className={isActive(button.path) ? 'active' : ''}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`sidebar.taskTrackingButtons.${button.id}`)}
                      primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                    />
                  </SubMenuItem>
                )
              )}
            </Collapse>
          </>
        )}

        {/* İnsan Kaynakları */}
        {visibleSections.hr && (
          <>
            <GroupHeader onClick={toggleHr} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <SupervisedUserCircleIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.humanResources')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {hrOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={hrOpen} timeout="auto" unmountOnExit>
              {HR_BUTTONS.length > 0 ? (
                HR_BUTTONS.map((button) => 
                  !TEMP_HIDDEN_HR_BUTTON_IDS.has(button.id) &&
                  visibleButtons.includes(button.id) &&
                  isItemVisible('hr', button.id, getLegacyAliases('hr', button.id)) && (
                    <SubMenuItem 
                      key={button.id}
                      className={isActive(button.path) ? 'active' : ''}
                      onClick={() => handleNavigation(button.path)}
                    >
                      <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                        {button.icon}
                      </ListItemIcon>
                      <ListItemText 
                        primary={t(`sidebar.hrButtons.${button.id}`)}
                        primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                      />
                    </SubMenuItem>
                  )
                )
              ) : (
                <SubMenuItem sx={{ fontStyle: 'italic', opacity: 0.7 }}>
                  <ListItemText primary={t('sidebar.comingSoonMenu')} />
                </SubMenuItem>
              )}
            </Collapse>
          </>
        )}

        {/* Akıllı Depo WMS */}
        {visibleSections.wms && (
          <>
            <GroupHeader onClick={toggleWms} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <WarehouseIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.smartWarehouseWms')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {wmsOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={wmsOpen} timeout="auto" unmountOnExit>
              {WMS_BUTTONS.map((button) => (
                isItemVisible('wms', button.id) && (
                  <SubMenuItem 
                    key={button.id}
                    className={isActive(button.path) ? 'active' : ''}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`sidebar.wmsButtons.${button.id}`)}
                      primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                    />
                  </SubMenuItem>
                )
              ))}
            </Collapse>
          </>
        )}

        {/* Cihaz Yönetimi */}
        {visibleSections.device_management && (
          <>
            <GroupHeader onClick={toggleDeviceManagement} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <DevicesIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.deviceManagement')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {deviceManagementOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={deviceManagementOpen} timeout="auto" unmountOnExit>
              {DEVICE_MANAGEMENT_BUTTONS.map((button) => (
                isItemVisible('device_management', button.id) && (
                  <SubMenuItem 
                    key={button.id}
                    className={isActive(button.path) ? 'active' : ''}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`sidebar.deviceManagementButtons.${button.id}`)}
                      primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                    />
                  </SubMenuItem>
                )
              ))}
            </Collapse>
          </>
        )}

        {/* Kart & Cüzdan */}
        {visibleSections.card_management && (
          <>
            <GroupHeader onClick={toggleCardManagement} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <CreditCardIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.cardManagement')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {cardManagementOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={cardManagementOpen} timeout="auto" unmountOnExit>
              {CARD_MANAGEMENT_BUTTONS.map((button) => (
                isItemVisible('card_management', button.id) && (
                  <SubMenuItem 
                    key={button.id}
                    className={isActive(button.path) ? 'active' : ''}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`sidebar.cardManagementButtons.${button.id}`)}
                      primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                    />
                  </SubMenuItem>
                )
              ))}
            </Collapse>
          </>
        )}

        {/* Şubeler */}
        {visibleSections.branches && (
          <>
            <GroupHeader onClick={toggleBranches} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <BusinessIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.branches')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {branchesOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={branchesOpen} timeout="auto" unmountOnExit>
              {BRANCHES_BUTTONS.map((button) => (
                isItemVisible('branches', button.id) && (
                  <SubMenuItem 
                    key={button.id}
                    className={isActive(button.path) ? 'active' : ''}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`sidebar.branchesButtons.${button.id}`)}
                      primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                    />
                  </SubMenuItem>
                )
              ))}
            </Collapse>
          </>
        )}

        {/* Diğer */}
        {visibleSections.other && (
          <>
            <GroupHeader onClick={toggleOther} sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                  <EventIcon />
                </ListItemIcon>
                <ListItemText 
                  primary={t('sidebar.other')}
                  primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)', fontWeight: 'bold' }}
                />
              </Box>
              {otherOpen ? <ExpandLess /> : <ExpandMore />}
            </GroupHeader>
            <Collapse in={otherOpen} timeout="auto" unmountOnExit>
              {OTHER_BUTTONS.map((button) => (
                !TEMP_HIDDEN_OTHER_BUTTON_IDS.has(button.id) &&
                (button.id !== 'users' || userDetails?.role === 'admin') &&
                isItemVisible('other', button.id) && (
                  <SubMenuItem 
                    key={button.id}
                    className={isActive(button.path) ? 'active' : ''}
                    onClick={() => handleNavigation(button.path)}
                  >
                    <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
                      {button.icon}
                    </ListItemIcon>
                    <ListItemText 
                      primary={t(`sidebar.otherButtons.${button.id}`)}
                      primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
                    />
                  </SubMenuItem>
                )
              ))}
            </Collapse>
          </>
        )}
        
        <Divider sx={{ my: 2, backgroundColor: 'rgba(255,255,255,0.1)' }} />
        
        <MenuItem onClick={handleOpenSettings}>
          <ListItemIcon sx={{ color: 'var(--sidebar-text-color, white)', minWidth: 40 }}>
            <SettingsIcon />
          </ListItemIcon>
          <ListItemText 
            primary={t('sidebar.settings')}
            primaryTypographyProps={{ color: 'var(--sidebar-text-color, white)' }}
          />
        </MenuItem>
      </MenuList>
      
      <MenuFooter>
        <Typography variant="caption" color="var(--sidebar-text-color, white)" sx={{ opacity: 0.7 }}>
          {currentUser?.email}
        </Typography>
        <IconButton 
          onClick={handleLogout}
          sx={{ color: 'var(--sidebar-text-color, white)' }}
        >
          <ExitToAppIcon />
        </IconButton>
      </MenuFooter>
      
      {/* Ayarlar modalı */}
      {settingsModalOpen && (
        <SidebarSettingsModal 
          open={settingsModalOpen} 
          onClose={() => setSettingsModalOpen(false)} 
        />
      )}
    </MenuContainer>
  );
};

export default MobileMenu; 